import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, RefreshCw, AlertTriangle, Mail, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtBusinessDate } from "@/lib/timezone";
import SearchAutocomplete, { type Suggestion } from "@/components/admin/SearchAutocomplete";


type FailureRow = {
  id: string;
  user_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  booking_date: string | null;
  booking_time: string | null;
  service_name: string | null;
  service_category: string | null;
  addons: string | null;
  price: number | null;
  stage: string;
  reason: string;
  error_code: string | null;
  source: string;
  created_at: string;
};

type EmailRow = {
  id: string;
  user_id: string | null;
  recipient_email: string;
  email_type: string;
  subject: string | null;
  html_body: string | null;
  status: string;
  error_message: string | null;
  provider_message_id: string | null;
  sent_at: string;
  created_at: string;
  metadata: { booking_id?: string } | null;
  booking_date?: string | null;
  booking_time?: string | null;
  service_name?: string | null;
  booking_created_at?: string | null;
};

const STAGES: Record<string, string> = {
  blocked_slot: "Blocked date/time",
  insert_admin: "Admin booking save",
  insert_no_payment: "Booking save (no payment)",
  checkout: "Payment checkout",
  payment_abandoned: "Payment abandoned",
  reported: "Reported by client",
};

const EMAIL_TYPES: Record<string, string> = {
  booking_confirmation: "Appointment confirmation",
  booking_reminder: "Appointment reminder",
  booking_cancellation: "Cancellation",
  booking_completion: "Post-appointment / review",
  booking_reschedule_pending: "Reschedule request",
  reschedule_request: "Reschedule request (admin)",
  payment_receipt: "Payment receipt",
  coupon_expiry_reminder: "Coupon expiry reminder",
  credit_awarded: "Credit awarded",
  client_invite: "Client invite",
  auth_emails: "Account / auth email",
};

const stageLabel = (s: string) => STAGES[s] || s;
const typeLabel = (t: string) =>
  EMAIL_TYPES[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const est = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York" });

const csvDownload = (name: string, header: string[], lines: string[]) => {
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const q = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const DeliveryBadge = ({ status }: { status: string }) => {
  const ok = status === "sent" || status === "delivered";
  const warn = status === "suppressed" || status === "pending";
  return (
    <Badge
      variant="outline"
      className={`rounded-none font-light whitespace-nowrap ${
        ok
          ? "border-primary/50 text-primary"
          : warn
            ? "border-muted-foreground/50 text-muted-foreground"
            : "border-destructive/50 text-destructive"
      }`}
    >
      {ok ? <CheckCircle2 size={11} className="mr-1" /> : <XCircle size={11} className="mr-1" />}
      {status}
    </Badge>
  );
};

const CommunicationData = () => {
  const [rows, setRows] = useState<FailureRow[]>([]);
  const [emails, setEmails] = useState<EmailRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Incomplete bookings filters
  const [stage, setStage] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Email filters
  const [eType, setEType] = useState("all");
  const [eStatus, setEStatus] = useState("all");
  const [eSearch, setESearch] = useState("");
  const [eSubject, setESubject] = useState("");

  const [eStart, setEStart] = useState("");
  const [eEnd, setEEnd] = useState("");

  const [previewEmail, setPreviewEmail] = useState<EmailRow | null>(null);

  const [clients, setClients] = useState<{ full_name: string | null; email: string | null }[]>([]);

  const load = async () => {
    setLoading(true);
    const [f, e, p] = await Promise.all([
      supabase.from("booking_failures").select("*").order("created_at", { ascending: false }).limit(1000),
      supabase.from("email_logs").select("*").order("sent_at", { ascending: false }).limit(1000),
      supabase.from("profiles").select("full_name, email").limit(2000),
    ]);
    if (f.error) toast.error("Failed to load incomplete bookings");
    else setRows((f.data ?? []) as FailureRow[]);

    if (e.error) {
      toast.error("Failed to load email history");
    } else {
      const emailData = (e.data ?? []) as EmailRow[];
      const bookingIds = Array.from(
        new Set(emailData.map((e) => e.metadata?.booking_id).filter(Boolean)),
      ) as string[];
      let bookingMap = new Map<
        string,
        { booking_date: string; booking_time: string; service_name: string; created_at: string }
      >();
      if (bookingIds.length) {
        const { data: bookings } = await supabase
          .from("bookings")
          .select("id, booking_date, booking_time, service_name, created_at").is("deleted_at", null)
          .in("id", bookingIds);
        (bookings ?? []).forEach((b) => {
          bookingMap.set(b.id, b);
        });
      }
      setEmails(
        emailData.map((e) => {
          const b = e.metadata?.booking_id ? bookingMap.get(e.metadata.booking_id) : undefined;
          return {
            ...e,
            booking_date: b?.booking_date ?? null,
            booking_time: b?.booking_time ?? null,
            service_name: b?.service_name ?? null,
            booking_created_at: b?.created_at ?? null,
          };
        }),
      );
    }

    if (!p.error) setClients((p.data ?? []) as { full_name: string | null; email: string | null }[]);
    setLoading(false);
  };

  const nameByEmail = useMemo(() => {
    const m = new Map<string, string>();
    clients.forEach((c) => {
      if (c.email && c.full_name) m.set(c.email.toLowerCase(), c.full_name);
    });
    return m;
  }, [clients]);

  const failureSuggestions = useMemo<Suggestion[]>(() => {
    const seen = new Map<string, Suggestion>();
    rows.forEach((r) => {
      if (r.client_name) seen.set(`n:${r.client_name}`, { value: r.client_name, label: r.client_name, hint: r.client_email ?? "Client" });
      if (r.client_email) seen.set(`e:${r.client_email}`, { value: r.client_email, label: r.client_email, hint: "Email" });
      if (r.service_name) seen.set(`s:${r.service_name}`, { value: r.service_name, label: r.service_name, hint: "Service" });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const clientSuggestions = useMemo<Suggestion[]>(() => {
    const seen = new Map<string, Suggestion>();
    emails.forEach((e) => {
      if (!e.recipient_email) return;
      const key = e.recipient_email.toLowerCase();
      const name = nameByEmail.get(key);
      if (name) seen.set(`n:${name}`, { value: name, label: name, hint: e.recipient_email });
      seen.set(`e:${key}`, { value: e.recipient_email, label: e.recipient_email, hint: name ?? "Recipient" });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [emails, nameByEmail]);

  const subjectSuggestions = useMemo<Suggestion[]>(() => {
    const seen = new Map<string, Suggestion>();
    emails.forEach((e) => {
      if (e.subject) seen.set(e.subject, { value: e.subject, label: e.subject, hint: "Subject" });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [emails]);



  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (stage !== "all" && r.stage !== stage) return false;
      if (startDate && r.created_at.slice(0, 10) < startDate) return false;
      if (endDate && r.created_at.slice(0, 10) > endDate) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [r.client_name, r.client_email, r.client_phone, r.service_name, r.reason]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [rows, stage, search, startDate, endDate]);

  const byStage = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach((r) => (counts[r.stage] = (counts[r.stage] || 0) + 1));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const emailTypeOptions = useMemo(
    () => Array.from(new Set(emails.map((e) => e.email_type))).sort(),
    [emails],
  );
  const emailStatusOptions = useMemo(
    () => Array.from(new Set(emails.map((e) => e.status))).sort(),
    [emails],
  );

  const filteredEmails = useMemo(() => {
    return emails.filter((e) => {
      if (eType !== "all" && e.email_type !== eType) return false;
      if (eStatus !== "all" && e.status !== eStatus) return false;
      const day = (e.sent_at || e.created_at).slice(0, 10);
      if (eStart && day < eStart) return false;
      if (eEnd && day > eEnd) return false;
      if (eSearch) {
        const s = eSearch.toLowerCase();
        const hay = [e.recipient_email, nameByEmail.get((e.recipient_email || "").toLowerCase())]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (eSubject) {
        if (!(e.subject || "").toLowerCase().includes(eSubject.toLowerCase())) return false;
      }
      return true;
    });
  }, [emails, eType, eStatus, eSearch, eSubject, eStart, eEnd, nameByEmail]);


  const emailCounts = useMemo(() => {
    const byType: Record<string, number> = {};
    let sent = 0;
    let failed = 0;
    filteredEmails.forEach((e) => {
      byType[e.email_type] = (byType[e.email_type] || 0) + 1;
      if (e.status === "sent" || e.status === "delivered") sent++;
      else failed++;
    });
    return { byType: Object.entries(byType).sort((a, b) => b[1] - a[1]), sent, failed };
  }, [filteredEmails]);

  const exportFailures = () =>
    csvDownload(
      "incomplete-bookings",
      ["Logged at", "Client", "Email", "Phone", "Requested date", "Requested time", "Service", "Add-ons", "Price", "Stage", "Reason", "Error code"],
      filtered.map((r) =>
        [r.created_at, r.client_name, r.client_email, r.client_phone, r.booking_date, r.booking_time, r.service_name, r.addons, r.price, stageLabel(r.stage), r.reason, r.error_code]
          .map(q)
          .join(","),
      ),
    );

  const exportEmails = () =>
    csvDownload(
      "email-history",
      ["Sent at", "Type", "Recipient", "Subject", "Appointment date", "Appointment time", "Service", "Booked on", "Delivery status", "Error", "Provider message id"],
      filteredEmails.map((e) =>
        [e.sent_at, typeLabel(e.email_type), e.recipient_email, e.subject, e.booking_date, e.booking_time, e.service_name, e.booking_created_at, e.status, e.error_message, e.provider_message_id]
          .map(q)
          .join(","),
      ),
    );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Bookings &amp; Communication Data</h1>
          <p className="text-sm text-muted-foreground font-light mt-1">
            Incomplete booking attempts and every email sent, with delivery status.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="incomplete">
        <TabsList className="rounded-none">
          <TabsTrigger value="incomplete" className="rounded-none">
            Incomplete Bookings ({rows.length})
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-none">
            Emails Sent ({emails.length})
          </TabsTrigger>
        </TabsList>

        {/* ---------- INCOMPLETE BOOKINGS ---------- */}
        <TabsContent value="incomplete" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2 items-center">
            {byStage.map(([s, n]) => (
              <Badge key={s} variant="outline" className="rounded-none font-light">
                {stageLabel(s)}: {n}
              </Badge>
            ))}
            <Button variant="outline" size="sm" className="rounded-none ml-auto" onClick={exportFailures} disabled={!filtered.length}>
              <Download size={14} />
              Export CSV
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 bg-card border border-border/50 p-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Search</Label>
              <SearchAutocomplete
                value={search}
                onChange={setSearch}
                suggestions={failureSuggestions}
                placeholder="Client, service, reason"
              />

            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {Object.entries(STAGES).map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-none" />
            </div>
          </div>

          <div className="bg-card border border-border/50 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Logged</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-light">
                      No incomplete booking attempts recorded.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{est(r.created_at)}</TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{r.client_name || "Unknown"}</div>
                        <div className="text-xs text-muted-foreground">{r.client_email || r.client_phone || "—"}</div>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {r.booking_date ? fmtBusinessDate(r.booking_date) : "—"}
                        <div className="text-xs text-muted-foreground">{r.booking_time || "—"}</div>
                      </TableCell>
                      <TableCell className="text-sm max-w-[240px]">
                        <div>{r.service_name || "—"}</div>
                        {r.addons && <div className="text-xs text-muted-foreground">+ {r.addons}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-none font-light whitespace-nowrap">
                          <AlertTriangle size={11} className="mr-1" />
                          {stageLabel(r.stage)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[320px] text-muted-foreground">
                        {r.reason}
                        {r.error_code && <span className="ml-1 opacity-60">({r.error_code})</span>}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- EMAILS ---------- */}
        <TabsContent value="emails" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Badge variant="outline" className="rounded-none font-light border-primary/50 text-primary">
              Delivered: {emailCounts.sent}
            </Badge>
            <Badge variant="outline" className="rounded-none font-light border-destructive/50 text-destructive">
              Failed: {emailCounts.failed}
            </Badge>
            {emailCounts.byType.map(([t, n]) => (
              <Badge key={t} variant="outline" className="rounded-none font-light">
                {typeLabel(t)}: {n}
              </Badge>
            ))}
            <Button variant="outline" size="sm" className="rounded-none ml-auto" onClick={exportEmails} disabled={!filteredEmails.length}>
              <Download size={14} />
              Export CSV
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6 bg-card border border-border/50 p-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Client</Label>
              <SearchAutocomplete
                value={eSearch}
                onChange={setESearch}
                suggestions={clientSuggestions}
                placeholder="Name or email"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Subject</Label>
              <SearchAutocomplete
                value={eSubject}
                onChange={setESubject}
                suggestions={subjectSuggestions}
                placeholder="Subject line"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Email type</Label>
              <Select value={eType} onValueChange={setEType}>
                <SelectTrigger className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {emailTypeOptions.map((t) => (
                    <SelectItem key={t} value={t}>
                      {typeLabel(t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Delivery</Label>
              <Select value={eStatus} onValueChange={setEStatus}>
                <SelectTrigger className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {emailStatusOptions.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">From</Label>
              <Input type="date" value={eStart} onChange={(e) => setEStart(e.target.value)} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">To</Label>
              <Input type="date" value={eEnd} onChange={(e) => setEEnd(e.target.value)} className="rounded-none" />
            </div>
          </div>

          <div className="bg-card border border-border/50 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sent</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Appointment</TableHead>
                  <TableHead>Delivery</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : filteredEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-light">
                      No emails match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmails.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{est(e.sent_at || e.created_at)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="rounded-none font-light whitespace-nowrap cursor-pointer hover:bg-accent"
                          title="Click to preview email"
                          onClick={() => setPreviewEmail(e)}
                        >
                          <Mail size={11} className="mr-1" />
                          {typeLabel(e.email_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{e.recipient_email}</TableCell>
                      <TableCell className="text-sm max-w-[320px]">
                        <div>{e.subject || "—"}</div>
                        {e.error_message && <div className="text-xs text-destructive mt-0.5">{e.error_message}</div>}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {e.booking_date ? (
                          <>
                            {fmtBusinessDate(e.booking_date)}
                            <div className="text-xs text-muted-foreground">{e.booking_time || "—"}</div>
                            {e.service_name && <div className="text-xs text-muted-foreground truncate max-w-[180px]">{e.service_name}</div>}
                            {e.booking_created_at && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Booked {new Date(e.booking_created_at).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" })}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DeliveryBadge status={e.status} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!previewEmail} onOpenChange={(open) => !open && setPreviewEmail(null)}>
        <DialogContent className="max-w-4xl rounded-none p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-serif text-2xl font-bold italic">Email preview</DialogTitle>
            {previewEmail && (
              <p className="text-sm text-muted-foreground font-light">
                {previewEmail.subject || "No subject"} · {previewEmail.recipient_email} · {est(previewEmail.sent_at || previewEmail.created_at)}
              </p>
            )}
          </DialogHeader>
          <div className="p-6 pt-4">
            {previewEmail?.html_body ? (
              <iframe
                title="Email preview"
                srcDoc={previewEmail.html_body}
                className="w-full h-[60vh] border border-border/50 bg-white"
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground font-light">
                No HTML preview is available for this email.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationData;
